// Conteos centralizados de los badges del Home y dashboards. Todos aplican el corte
// FECHA_CORTE (1-ene-2026): las facturas/movimientos históricos pre-Clay no inflan los
// badges. Las facturas recibidas excluyen NCs (tipo_dte=61): reducen deuda, no son DTEs
// pendientes pagables. La regla vive acá una sola vez — consumidores: Admin Home y
// DashboardFinanciero.
import {
  CLASIFICACION,
  TIPO_DOCUMENTO,
  derivarClasificacion,
  normalizarExterna,
  normalizarHonorario,
  normalizarSiiRecibida,
} from "./compras/documentosCosto";
import { FECHA_CORTE } from "./estadoCuenta/saldoInicial";
import { getOperacionesFactoring } from "./factoring";
import { getMovimientosBancarios, isMovPendiente } from "./movimientosBancarios";
import { getPagosPorMovimientos } from "./pagosEmpleados";
import { client } from "./supabase";

// Facturas SII recibidas pendientes desde el corte, excluyendo NCs (tipo_dte=61).
export async function getCountRecibidasPendientes(pais) {
  const { count } = await client
    .from("facturas_sii_recibidas")
    .select("id", { count: "exact", head: true })
    .eq("pais", pais)
    .eq("estado_vinculacion", "pendiente")
    .gte("fecha_emision", FECHA_CORTE)
    .neq("tipo_dte", 61);
  return count || 0;
}

// Facturas de COMPRA SIN CLASIFICAR (eje CONTABLE) — el mismo número que la card "SIN CLASIFICAR"
// de ComprasTab. Reproduce EXACTO su universo: las 3 fuentes (SII recibidas + externas + honorarios
// que computan) con los MISMOS filtros de `useDocumentosCosto` (país, corte FECHA_CORTE en sii/
// honorarios, externas no anuladas), normalizadas con las MISMAS funciones puras y pasadas por el
// MISMO `derivarClasificacion`; se excluyen las NC (no se clasifican). Se reusan las funciones de
// `documentosCosto` a propósito: un solo criterio, sin riesgo de divergir del número de la card.
export async function getCountComprasSinClasificar(pais) {
  const [sii, externas, honorarios] = await Promise.all([
    client
      .from("facturas_sii_recibidas")
      .select("id, tipo_dte, estado_vinculacion, tipo_contable, cuenta_er, fecha_emision")
      .eq("pais", pais)
      .gte("fecha_emision", FECHA_CORTE),
    client.from("facturas_externas").select("id, estado, cuenta_er").eq("pais", pais).neq("estado", "anulada"),
    client
      .from("boletas_honorarios")
      .select("id, computa, tipo_contable, cuenta_er, fecha_emision")
      .eq("pais", pais)
      .gte("fecha_emision", FECHA_CORTE),
  ]);

  const docs = [
    ...(sii.data || []).map((f) => normalizarSiiRecibida(f)),
    ...(externas.data || []).map(normalizarExterna),
    ...(honorarios.data || []).filter((b) => b.computa).map(normalizarHonorario),
  ];

  return docs.filter(
    (d) => d.tipo !== TIPO_DOCUMENTO.NOTA_CREDITO && derivarClasificacion(d).estado === CLASIFICACION.SIN_CLASIFICAR
  ).length;
}

// ─── Badge del tile "Caja" = EXACTAMENTE lo que se ve al entrar a /admin/flujo-caja ──────────
//
// El tile NO tiene criterio propio: muestra lo mismo que los badges visibles del panel Históricos.
// Desde 2026-08-29 los pendientes de vincular se muestran partidos por dirección (Ingresos/Egresos)
// en Históricos, y el badge de "Por pagar" de Futuros se quitó (la vinculación de facturas a
// proveedores se resuelve en Facturación > Compras; el contador ya no aporta). Por eso el tile suma
// SOLO los movimientos pendientes de vincular de todas las monedas (= ingresos_pend + egresos_pend);
// ya NO suma "Por pagar".
//
// Para no divergir (regla 10.1) reusa el MISMO predicado `isMovPendiente` que el componente y los
// mismos loaders (getMovimientosBancarios / getPagosPorMovimientos / getOperacionesFactoring). Es un
// orquestador self-contained: arma los sets con SU propio fetch en vez de recibir el state de React.

// Total de movimientos pendientes de vincular, TODAS las monedas. Espeja el cómputo de `movPendientes`
// del panel Históricos (que en pantalla se muestra partido en pendientesIngreso/pendientesEgreso):
// mismo universo (getMovimientosBancarios desde el corte), mismos sets (pagos de empleado + factoring
// adelanto/remanente/puente) y el MISMO predicado isMovPendiente. Contar todos los que pasan el
// predicado = ingresos_pend + egresos_pend.
async function getCountMovimientosPendientesCaja(pais) {
  const movs = await getMovimientosBancarios(pais, { fechaDesde: FECHA_CORTE });

  // pagosMovIds: ids vinculados a un pago de empleado (mismo helper que el componente).
  const pagosEmp = await getPagosPorMovimientos(movs.map((m) => m.id));
  const pagosMovIds = new Set((pagosEmp || []).map((p) => p.movimiento_bancario_id).filter(Boolean));

  // Fase 3.1 + R3.4: movsVinculadosLibro — ids con CUALQUIER evento en el libro `aplicaciones_saldo`
  // por origen bancario (eje cobro: sii_emitida; eje pago: sii_recibida/factura_externa/boleta_honorario
  // — desde 1.2a/1.2b externas y boletas también viven en el libro). MISMO universo que el badge V/C
  // (sii_emitida/sii_recibida) del panel (10.229) más el resto del eje pago que ese badge no cubre; el
  // tile no debe divergir del panel Históricos. Fetch propio del tile (self-contained, igual que los
  // demás sets de arriba); default vacío en isMovPendiente sería contar de más (los MBF-sin-caja
  // seguirían "pendientes").
  const { data: libroOrigen } = await client
    .from("aplicaciones_saldo")
    .select("origen_id, documento_tipo")
    .eq("origen_tipo", "movimiento_bancario")
    .range(0, 49999);
  // R3.4: los 4 documento_tipo del libro cubren los dos ejes (sii_emitida=cobro;
  // sii_recibida/factura_externa/boleta_honorario=pago) — antes solo se miraban los dos de SII.
  const DOCUMENTO_TIPOS_LIBRO = new Set(["sii_emitida", "sii_recibida", "factura_externa", "boleta_honorario"]);
  const movsVinculadosLibro = new Set(
    (libroOrigen || [])
      .filter((a) => a.origen_id != null && DOCUMENTO_TIPOS_LIBRO.has(a.documento_tipo))
      .map((a) => a.origen_id)
  );

  // Set de factoring: adelanto + remanente (FKs legacy) + puente operaciones_factoring_movimientos.
  // getOperacionesFactoring sin filtro de estado (igual que el componente): incluye ops liquidadas.
  const ops = await getOperacionesFactoring(pais).catch(() => []);
  const factoringMovIds = new Set();
  for (const op of ops || []) {
    if (op.movimiento_bancario_adelanto_id) factoringMovIds.add(op.movimiento_bancario_adelanto_id);
    if (op.movimiento_bancario_remanente_id) factoringMovIds.add(op.movimiento_bancario_remanente_id);
    for (const mv of op.movimientos_vinculados || []) {
      if (mv.movimiento?.id) factoringMovIds.add(mv.movimiento.id);
    }
  }

  return movs.filter((m) => isMovPendiente(m, pagosMovIds, factoringMovIds, movsVinculadosLibro)).length;
}

// Badge del tile "Caja": movimientos pendientes de vincular de todas las monedas (= ingresos_pend +
// egresos_pend que se ven en Históricos). Ya NO suma "Por pagar" (badge quitado, decisión 2026-08-29).
export async function getCountCajaPendientes(pais) {
  return getCountMovimientosPendientesCaja(pais);
}
